import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { TableFooter, TablePagination } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { styled, useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import * as React from "react";
import { ISO639Lang } from "../../constans/languagesList";
import TripleDotPoperMenu from "../../components/TripleDotPoperMenu/TripleDotPoperMenu";
import { Quiz } from "../../types/Quiz";
import { getQuizQuestionCount } from "../../utils/quizChapters";
import {
  evaluationTypeLabels,
  getQuizDisplayTitle,
  quizTypeLabels,
} from "./systemQuizModel";
import s from "./SystemQuizzes.module.css";

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={(event) => onPageChange(event, 0)}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? (
          <LastPageIcon color="success" />
        ) : (
          <FirstPageIcon color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={(event) => onPageChange(event, page - 1)}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight color="success" />
        ) : (
          <KeyboardArrowLeft color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={(event) => onPageChange(event, page + 1)}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft color="success" />
        ) : (
          <KeyboardArrowRight color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={(event) =>
          onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
        }
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? (
          <FirstPageIcon color="success" />
        ) : (
          <LastPageIcon color="success" />
        )}
      </IconButton>
    </Box>
  );
}

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#313131",
    color: "white",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: "white",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#2E2E2E",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "#242424",
    border: 0,
  },
  "td, th": {
    border: 0,
  },
}));

type SystemQuizzesTableProps = {
  rows: Quiz[];
  page: number;
  rowsPerPage: number;
  count: number;
  setPage: (page: number) => void;
  setRowsPerPage: (limit: number) => void;
  onSelectQuiz: (quiz: Quiz) => void;
  onEditQuiz: (quiz: Quiz) => void;
  onDeleteQuiz: (quiz: Quiz) => void;
};

const SystemQuizzesTable = ({
  rows,
  page,
  rowsPerPage,
  count,
  setPage,
  setRowsPerPage,
  onSelectQuiz,
  onEditQuiz,
  onDeleteQuiz,
}: SystemQuizzesTableProps) => {
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        minWidth: 700,
        width: "100%",
        borderRadius: "20px",
        backgroundColor: "transparent",
      }}
    >
      <Table sx={{ minWidth: 700, width: "100%" }} aria-label="system quizzes">
        <TableHead>
          <TableRow>
            <StyledTableCell align="left">Title</StyledTableCell>
            <StyledTableCell align="left">Type</StyledTableCell>
            <StyledTableCell align="left">Evaluation</StyledTableCell>
            <StyledTableCell align="left">Languages</StyledTableCell>
            <StyledTableCell align="left">Questions</StyledTableCell>
            <StyledTableCell align="left">Minimum Points</StyledTableCell>
            <StyledTableCell align="left">Retakes</StyledTableCell>
            <StyledTableCell align="right">Actions</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <StyledTableRow
              key={row._id}
              sx={{ cursor: "pointer" }}
              onClick={() => onSelectQuiz(row)}
            >
              <StyledTableCell>{getQuizDisplayTitle(row)}</StyledTableCell>
              <StyledTableCell>
                {row.type ? quizTypeLabels[row.type] : "No type"}
              </StyledTableCell>
              <StyledTableCell>
                {row.evaluationType
                  ? evaluationTypeLabels[row.evaluationType]
                  : "Not set"}
              </StyledTableCell>
              <StyledTableCell>
                {(row.title ?? [])
                  .map((item) => ISO639Lang[item.language])
                  .join(", ")}
              </StyledTableCell>
              <StyledTableCell>
                {getQuizQuestionCount(row.quiz)}
              </StyledTableCell>
              <StyledTableCell>
                {row.quiz?.minimumPoints ?? "-"}
              </StyledTableCell>
              <StyledTableCell>{row.quiz?.retakeAmount ?? "-"}</StyledTableCell>
              <StyledTableCell align="right">
                <div
                  className={s.tableActions}
                  onClick={(event) => event.stopPropagation()}
                >
                  <TripleDotPoperMenu
                    menuItemDta={[
                      {
                        title: "Update",
                        action: () => onEditQuiz(row),
                      },
                      {
                        title: "Delete",
                        action: () => onDeleteQuiz(row),
                        styles: { color: "red" },
                      },
                    ]}
                  />
                </div>
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
        <TableFooter className={s.tableFooter}>
          <TableRow sx={{ minWidth: 700, width: "100%", border: "none" }}>
            <TablePagination
              rowsPerPageOptions={[10, 20, 30]}
              colSpan={8}
              count={count}
              rowsPerPage={rowsPerPage}
              page={page - 1}
              slotProps={{
                select: {
                  inputProps: {
                    "aria-label": "rows per page",
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              sx={{ border: "none", color: "white" }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default SystemQuizzesTable;
